import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import {
  customElement,
  type TemplateResult,
  property,
  html,
  cssManager,
  css,
  state,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesModal } from '../../00group-overlay/dees-modal/dees-modal.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-layout/dees-label/dees-label.js';
import '../../00group-layout/dees-tile/dees-tile.js';
import '../../00group-workspace/dees-workspace-monaco/dees-workspace-monaco.js';
import { DeesWorkspaceMonaco } from '../../00group-workspace/dees-workspace-monaco/dees-workspace-monaco.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-code': DeesInputCode;
  }
}

// Common programming languages for the language selector
const LANGUAGES = [
  { key: 'typescript', label: 'TypeScript' },
  { key: 'javascript', label: 'JavaScript' },
  { key: 'json', label: 'JSON' },
  { key: 'html', label: 'HTML' },
  { key: 'css', label: 'CSS' },
  { key: 'scss', label: 'SCSS' },
  { key: 'markdown', label: 'Markdown' },
  { key: 'yaml', label: 'YAML' },
  { key: 'xml', label: 'XML' },
  { key: 'sql', label: 'SQL' },
  { key: 'python', label: 'Python' },
  { key: 'java', label: 'Java' },
  { key: 'csharp', label: 'C#' },
  { key: 'cpp', label: 'C++' },
  { key: 'go', label: 'Go' },
  { key: 'rust', label: 'Rust' },
  { key: 'shell', label: 'Shell' },
  { key: 'plaintext', label: 'Plain Text' },
];

@customElement('dees-input-code')
export class DeesInputCode extends DeesInputBase<string> {
  public static demo = () => html`
    <dees-input-code
      label="TypeScript Code"
      key="code"
      language="typescript"
      height="300px"
      .value=${'const greeting: string = "Hello World";\nconsole.log(greeting);'}
    ></dees-input-code>
  `;
  public static demoGroups = ['Input', 'Workspace'];

  // INSTANCE
  @property({ type: String })
  accessor value: string = '';

  @property({ type: String })
  accessor language: string = 'typescript';

  @property({ type: String })
  accessor height: string = '200px';

  @property({ type: String })
  accessor wordWrap: 'on' | 'off' = 'off';

  @property({ type: Boolean })
  accessor showLineNumbers: boolean = true;

  @state()
  accessor isLanguageDropdownOpen: boolean = false;

  @state()
  accessor copySuccess: boolean = false;

  @state()
  accessor lineCount: number = 0;

  @state()
  accessor cursorPosition: { line: number; column: number } = { line: 1, column: 1 };

  private editorElement: DeesWorkspaceMonaco | null = null;

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      * {
        box-sizing: border-box;
      }

      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .input-wrapper,
      :host([label-position="top"]) .input-wrapper,
      :host([label-position="left"]) .input-wrapper,
      :host([label-position="right"]) .input-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }

      dees-tile {
        flex: 1;
        min-height: 0;
      }

      .toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 12px;
        gap: 8px;
      }

      .toolbar-left {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .toolbar-right {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .language-selector {
        position: relative;
      }

      .language-button {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 500;
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        cursor: pointer;
        color: var(--dees-color-text-secondary);
        transition:
              background-color var(--dees-transition-fast) var(--dees-ease-standard),
              color var(--dees-transition-fast) var(--dees-ease-standard),
              border-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .language-button:hover {
        background: var(--dees-color-hover);
      }

      .language-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: 4px;
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        box-shadow: var(--dees-shadow-lg);
        z-index: 100;
        max-height: 250px;
        overflow-y: auto;
        min-width: 140px;
      }

      .language-option {
        padding: 8px 12px;
        font-size: 12px;
        cursor: pointer;
        color: var(--dees-color-text-secondary);
        transition: background-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .language-option:hover {
        background: var(--dees-color-hover);
      }

      .language-option.selected {
        background: var(--dees-color-active);
      }

      .toolbar-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: transparent;
        border: none;
        border-radius: var(--dees-radius-md);
        cursor: pointer;
        color: var(--dees-color-text-muted);
        transition:
              background-color var(--dees-transition-fast) var(--dees-ease-standard),
              color var(--dees-transition-fast) var(--dees-ease-standard),
              border-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .toolbar-button:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-secondary);
      }

      .toolbar-button.active {
        background: var(--dees-color-active);
        color: var(--dees-color-text-secondary);
      }

      .toolbar-button.success {
        color: var(--dees-color-text-success);
      }

      .editor-wrapper {
        position: absolute;
        inset: 0;
      }

      dees-workspace-monaco {
        display: block;
        height: 100%;
      }

      /* Match Monaco background to tile */
      dees-workspace-monaco::part(container) {
        background: var(--dees-color-bg-primary);
      }

      .toolbar-divider {
        width: 1px;
        height: 20px;
        background: var(--dees-color-border-default);
        margin: 0 4px;
      }

      /* Footer */
      .editor-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px;
        height: 28px;
        font-size: 11px;
        color: var(--dees-color-text-muted);
        width: 100%;
        box-sizing: border-box;
      }

      .footer-left,
      .footer-right {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .footer-separator {
        width: 1px;
        height: 12px;
        background: var(--dees-color-border-default);
      }

      :host([disabled]) .code-container {
        opacity: 0.5;
        pointer-events: none;
      }
    `,
  ];

  public render(): TemplateResult {
    const currentLanguage = LANGUAGES.find(l => l.key === this.language) || LANGUAGES[0];

    return html`
      <style>
        :host {
          height: ${this.height};
        }
      </style>
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>
        <dees-tile>
          <div slot="header" class="toolbar">
            <div class="toolbar-left">
              <div class="language-selector">
                <button
                  class="language-button"
                  @click=${this.toggleLanguageDropdown}
                  @blur=${this.handleLanguageBlur}
                >
                  ${currentLanguage.label}
                  <dees-icon .icon=${'lucide:ChevronDown'} iconSize="14"></dees-icon>
                </button>
                ${this.isLanguageDropdownOpen ? html`
                  <div class="language-dropdown">
                    ${LANGUAGES.map(lang => html`
                      <div
                        class="language-option ${lang.key === this.language ? 'selected' : ''}"
                        @mousedown=${(e: Event) => this.selectLanguage(e, lang.key)}
                      >
                        ${lang.label}
                      </div>
                    `)}
                  </div>
                ` : ''}
              </div>
            </div>
            <div class="toolbar-right">
              <button
                class="toolbar-button ${this.wordWrap === 'on' ? 'active' : ''}"
                title="Word Wrap"
                @click=${this.toggleWordWrap}
              >
                <dees-icon .icon=${'lucide:WrapText'} iconSize="16"></dees-icon>
              </button>
              <button
                class="toolbar-button ${this.showLineNumbers ? 'active' : ''}"
                title="Line Numbers"
                @click=${this.toggleLineNumbers}
              >
                <dees-icon .icon=${'lucide:Hash'} iconSize="16"></dees-icon>
              </button>
              <div class="toolbar-divider"></div>
              <button
                class="toolbar-button ${this.copySuccess ? 'success' : ''}"
                title="Copy Code"
                @click=${this.copyCode}
              >
                <dees-icon .icon=${this.copySuccess ? 'lucide:Check' : 'lucide:Copy'} iconSize="16"></dees-icon>
              </button>
              <button
                class="toolbar-button"
                title="Expand"
                @click=${this.openFullscreen}
              >
                <dees-icon .icon=${'lucide:Maximize2'} iconSize="16"></dees-icon>
              </button>
            </div>
          </div>
          <div class="editor-wrapper">
            <dees-workspace-monaco
              .content=${this.value}
              .language=${this.language}
              .wordWrap=${this.wordWrap}
              @content-change=${this.handleContentChange}
            ></dees-workspace-monaco>
          </div>
          <div slot="footer" class="editor-footer">
            <div class="footer-left">
              <span>Ln ${this.cursorPosition.line}, Col ${this.cursorPosition.column}</span>
              <div class="footer-separator"></div>
              <span>${this.lineCount} line${this.lineCount !== 1 ? 's' : ''}</span>
            </div>
            <div class="footer-right">
              <span>${(LANGUAGES.find(l => l.key === this.language) || LANGUAGES[0]).label}</span>
            </div>
          </div>
        </dees-tile>
        ${this.renderDescription()}
      </div>
    `;
  }

  async firstUpdated() {
    this.editorElement = this.shadowRoot?.querySelector('dees-workspace-monaco') as DeesWorkspaceMonaco;
    if (this.editorElement) {
      // Subscribe to content changes from the editor
      this.editorElement.contentSubject.subscribe((newContent: string) => {
        if (this.value !== newContent) {
          this.value = newContent;
          this.changeSubject.next(this as any);
        }
      });

      // Track cursor position and line count
      const editor = await this.editorElement.editorDeferred.promise;

      // Set initial line count
      const model = editor.getModel();
      if (model) {
        this.lineCount = model.getLineCount();
        model.onDidChangeContent(() => {
          this.lineCount = model.getLineCount();
        });
      }

      // Track cursor position
      editor.onDidChangeCursorPosition((e) => {
        this.cursorPosition = { line: e.position.lineNumber, column: e.position.column };
      });

      // Override Monaco editor background to match tile
      const domtoolsInstance = await this.editorElement.domtoolsPromise;
      const updateEditorBg = (isBright: boolean) => {
        const bg = isBright ? '#ffffff' : '#0a0a0a';
        editor.updateOptions({});
        // Override via Monaco's theme API
        (window as any).monaco?.editor?.defineTheme?.('dees-light', {
          base: 'vs',
          inherit: true,
          rules: [],
          colors: { 'editor.background': bg },
        });
        (window as any).monaco?.editor?.defineTheme?.('dees-dark', {
          base: 'vs-dark',
          inherit: true,
          rules: [],
          colors: { 'editor.background': bg },
        });
        editor.updateOptions({ theme: isBright ? 'dees-light' : 'dees-dark' });
      };

      updateEditorBg(domtoolsInstance.themeManager.goBrightBoolean);
      domtoolsInstance.themeManager.themeObservable.subscribe((goBright: boolean) => {
        updateEditorBg(goBright);
      });
    }
  }

  private toggleLanguageDropdown() {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  private handleLanguageBlur() {
    // Small delay to allow click events on dropdown items
    setTimeout(() => {
      this.isLanguageDropdownOpen = false;
    }, 150);
  }

  private async selectLanguage(e: Event, languageKey: string) {
    e.preventDefault();
    this.language = languageKey;
    this.isLanguageDropdownOpen = false;

    // Update the editor language
    if (this.editorElement) {
      this.editorElement.language = languageKey;
      const editor = await this.editorElement.editorDeferred.promise;
      const model = editor.getModel();
      if (model) {
        (window as any).monaco.editor.setModelLanguage(model, languageKey);
      }
    }
  }

  private toggleWordWrap() {
    this.wordWrap = this.wordWrap === 'on' ? 'off' : 'on';
    this.updateEditorOption('wordWrap', this.wordWrap);
  }

  private toggleLineNumbers() {
    this.showLineNumbers = !this.showLineNumbers;
    this.updateEditorOption('lineNumbers', this.showLineNumbers ? 'on' : 'off');
  }

  private async updateEditorOption(option: string, value: any) {
    if (this.editorElement) {
      const editor = await this.editorElement.editorDeferred.promise;
      editor.updateOptions({ [option]: value });
    }
  }

  private async copyCode() {
    try {
      await navigator.clipboard.writeText(this.value);
      this.copySuccess = true;
      setTimeout(() => {
        this.copySuccess = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  }

  private handleContentChange(e: CustomEvent) {
    const newContent = e.detail;
    if (this.value !== newContent) {
      this.value = newContent;
      this.changeSubject.next(this as any);
    }
  }

  public async openFullscreen() {
    const currentValue = this.value;
    let modalEditorElement: DeesWorkspaceMonaco | null = null;

    // Modal-specific state
    let modalLanguage = this.language;
    let modalWordWrap = this.wordWrap;
    let modalShowLineNumbers = this.showLineNumbers;
    let modalLanguageDropdownOpen = false;
    let modalCopySuccess = false;

    // Helper to get current language label
    const getLanguageLabel = () => {
      const lang = LANGUAGES.find(l => l.key === modalLanguage);
      return lang ? lang.label : 'TypeScript';
    };

    // Helper to update toolbar UI
    const updateToolbarUI = (modal: DeesModal) => {
      const toolbar = modal.shadowRoot?.querySelector('.modal-toolbar');
      if (!toolbar) return;

      const langBtn = toolbar.querySelector('.language-button span');
      if (langBtn) langBtn.textContent = getLanguageLabel();

      const wrapBtn = toolbar.querySelector('.wrap-btn') as HTMLElement;
      if (wrapBtn) wrapBtn.classList.toggle('active', modalWordWrap === 'on');

      const linesBtn = toolbar.querySelector('.lines-btn') as HTMLElement;
      if (linesBtn) linesBtn.classList.toggle('active', modalShowLineNumbers);

      const copyBtn = toolbar.querySelector('.copy-btn') as HTMLElement;
      const copyIcon = copyBtn?.querySelector('dees-icon') as any;
      if (copyBtn && copyIcon) {
        copyBtn.classList.toggle('success', modalCopySuccess);
        copyIcon.icon = modalCopySuccess ? 'lucide:Check' : 'lucide:Copy';
      }

      const dropdown = toolbar.querySelector('.language-dropdown') as HTMLElement;
      if (dropdown) dropdown.style.display = modalLanguageDropdownOpen ? 'block' : 'none';
    };

    // Helper to update footer UI
    const updateFooterUI = (modal: DeesModal) => {
      const footer = modal.shadowRoot?.querySelector('.modal-footer');
      if (!footer) return;

      const cursorEl = footer.querySelector('.footer-cursor');
      const linesEl = footer.querySelector('.footer-lines');
      const langEl = footer.querySelector('.footer-lang');

      if (cursorEl) cursorEl.textContent = `Ln ${modalCursorLine}, Col ${modalCursorCol}`;
      if (linesEl) linesEl.textContent = `${modalLineCount} line${modalLineCount !== 1 ? 's' : ''}`;
      if (langEl) langEl.textContent = getLanguageLabel();
    };

    let modalCursorLine = 1;
    let modalCursorCol = 1;
    let modalLineCount = currentValue.split('\n').length;

    const modal = await DeesModal.createAndShow({
      heading: this.label || 'Code Editor',
      width: 'fullscreen',
      contentPadding: 0,
      content: html`
        <style>
          .modal-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 12px;
            gap: 8px;
          }
          .modal-toolbar .toolbar-left {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .modal-toolbar .toolbar-right {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .modal-toolbar .language-selector {
            position: relative;
          }
          .modal-toolbar .language-button {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            font-size: 12px;
            font-weight: 500;
            background: var(--dees-color-bg-primary);
            border: 1px solid var(--dees-color-border-default);
            border-radius: var(--dees-radius-md);
            cursor: pointer;
            color: var(--dees-color-text-primary);
            transition:
              background-color var(--dees-transition-fast) var(--dees-ease-standard),
              color var(--dees-transition-fast) var(--dees-ease-standard),
              border-color var(--dees-transition-fast) var(--dees-ease-standard);
          }
          .modal-toolbar .language-button:hover {
            background: var(--dees-color-badge-default-bg);
          }
          .modal-toolbar .language-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            margin-top: 4px;
            background: var(--dees-color-bg-primary);
            border: 1px solid var(--dees-color-border-default);
            border-radius: var(--dees-radius-lg);
            box-shadow: var(--dees-shadow-lg);
            z-index: 100;
            max-height: 250px;
            overflow-y: auto;
            min-width: 140px;
            display: none;
          }
          .modal-toolbar .language-option {
            padding: 8px 12px;
            font-size: 12px;
            cursor: pointer;
            color: var(--dees-color-text-primary);
            transition: background-color var(--dees-transition-fast) var(--dees-ease-standard);
          }
          .modal-toolbar .language-option:hover {
            background: var(--dees-color-badge-default-bg);
          }
          .modal-toolbar .language-option.selected {
            background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
          }
          .modal-toolbar .toolbar-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: transparent;
            border: none;
            border-radius: var(--dees-radius-md);
            cursor: pointer;
            color: var(--dees-color-text-muted);
            transition:
              background-color var(--dees-transition-fast) var(--dees-ease-standard),
              color var(--dees-transition-fast) var(--dees-ease-standard),
              border-color var(--dees-transition-fast) var(--dees-ease-standard);
          }
          .modal-toolbar .toolbar-button:hover {
            background: var(--dees-color-border-default);
            color: var(--dees-color-text-primary);
          }
          .modal-toolbar .toolbar-button.active {
            background: color-mix(in srgb, var(--dees-color-text-primary) 13%, var(--dees-color-bg-primary));
            color: var(--dees-color-text-primary);
          }
          .modal-toolbar .toolbar-button.success {
            color: var(--dees-color-text-success);
          }
          .modal-toolbar .toolbar-divider {
            width: 1px;
            height: 20px;
            background: color-mix(in srgb, var(--dees-color-text-primary) 13%, var(--dees-color-bg-primary));
            margin: 0 4px;
          }
          .modal-editor-wrapper {
            position: relative;
            height: calc(100vh - 200px);
            width: 100%;
          }
          .modal-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 12px;
            height: 28px;
            font-size: 11px;
            color: var(--dees-color-text-muted);
            border-top: 1px solid var(--dees-color-border-default);
          }
          .modal-footer .footer-left,
          .modal-footer .footer-right {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .modal-footer .footer-separator {
            width: 1px;
            height: 12px;
            background: color-mix(in srgb, var(--dees-color-text-primary) 13%, var(--dees-color-bg-primary));
          }
        </style>
        <div class="modal-toolbar">
          <div class="toolbar-left">
            <div class="language-selector">
              <button class="language-button">
                <span>${getLanguageLabel()}</span>
                <dees-icon .icon=${'lucide:ChevronDown'} iconSize="14"></dees-icon>
              </button>
              <div class="language-dropdown">
                ${LANGUAGES.map(lang => html`
                  <div
                    class="language-option ${lang.key === modalLanguage ? 'selected' : ''}"
                    data-lang="${lang.key}"
                  >
                    ${lang.label}
                  </div>
                `)}
              </div>
            </div>
          </div>
          <div class="toolbar-right">
            <button class="toolbar-button wrap-btn ${modalWordWrap === 'on' ? 'active' : ''}" title="Word Wrap">
              <dees-icon .icon=${'lucide:WrapText'} iconSize="16"></dees-icon>
            </button>
            <button class="toolbar-button lines-btn ${modalShowLineNumbers ? 'active' : ''}" title="Line Numbers">
              <dees-icon .icon=${'lucide:Hash'} iconSize="16"></dees-icon>
            </button>
            <div class="toolbar-divider"></div>
            <button class="toolbar-button copy-btn" title="Copy Code">
              <dees-icon .icon=${'lucide:Copy'} iconSize="16"></dees-icon>
            </button>
          </div>
        </div>
        <div class="modal-editor-wrapper">
          <dees-workspace-monaco
            .content=${currentValue}
            .language=${modalLanguage}
            .wordWrap=${modalWordWrap}
          ></dees-workspace-monaco>
        </div>
        <div class="modal-footer">
          <div class="footer-left">
            <span class="footer-cursor">Ln ${modalCursorLine}, Col ${modalCursorCol}</span>
            <div class="footer-separator"></div>
            <span class="footer-lines">${modalLineCount} line${modalLineCount !== 1 ? 's' : ''}</span>
          </div>
          <div class="footer-right">
            <span class="footer-lang">${getLanguageLabel()}</span>
          </div>
        </div>
      `,
      menuOptions: [
        {
          name: 'Cancel',
          action: async (modalRef) => {
            await modalRef!.destroy();
          },
        },
        {
          name: 'Save & Close',
          action: async (modalRef) => {
            modalEditorElement = modalRef!.shadowRoot?.querySelector('dees-workspace-monaco') as DeesWorkspaceMonaco;
            if (modalEditorElement) {
              const editor = await modalEditorElement.editorDeferred.promise;
              const newValue = editor.getValue();
              this.setValue(newValue);
            }
            await modalRef!.destroy();
          },
        },
      ],
    });

    // Wait for modal to render
    await new Promise(resolve => setTimeout(resolve, 100));
    modalEditorElement = modal.shadowRoot?.querySelector('dees-workspace-monaco') as DeesWorkspaceMonaco;

    // Apply custom Monaco theme for matching background
    if (modalEditorElement) {
      const editor = await modalEditorElement.editorDeferred.promise;
      const domtoolsInstance = await modalEditorElement.domtoolsPromise;

      const applyModalTheme = (isBright: boolean) => {
        const bg = isBright ? '#ffffff' : '#0a0a0a';
        (window as any).monaco?.editor?.defineTheme?.('dees-light', {
          base: 'vs',
          inherit: true,
          rules: [],
          colors: { 'editor.background': bg },
        });
        (window as any).monaco?.editor?.defineTheme?.('dees-dark', {
          base: 'vs-dark',
          inherit: true,
          rules: [],
          colors: { 'editor.background': bg },
        });
        editor.updateOptions({ theme: isBright ? 'dees-light' : 'dees-dark' });
      };

      applyModalTheme(domtoolsInstance.themeManager.goBrightBoolean);
      domtoolsInstance.themeManager.themeObservable.subscribe((goBright: boolean) => {
        applyModalTheme(goBright);
      });

      // Track cursor position
      editor.onDidChangeCursorPosition((e) => {
        modalCursorLine = e.position.lineNumber;
        modalCursorCol = e.position.column;
        updateFooterUI(modal);
      });

      // Track line count
      const model = editor.getModel();
      if (model) {
        modalLineCount = model.getLineCount();
        updateFooterUI(modal);
        model.onDidChangeContent(() => {
          modalLineCount = model.getLineCount();
          updateFooterUI(modal);
        });
      }
    }

    // Wire up toolbar event handlers
    const toolbar = modal.shadowRoot?.querySelector('.modal-toolbar');
    if (toolbar) {
      const langBtn = toolbar.querySelector('.language-button');
      langBtn?.addEventListener('click', () => {
        modalLanguageDropdownOpen = !modalLanguageDropdownOpen;
        updateToolbarUI(modal);
      });

      const langOptions = toolbar.querySelectorAll('.language-option');
      langOptions.forEach((option) => {
        option.addEventListener('click', async () => {
          const newLang = (option as HTMLElement).dataset.lang;
          if (newLang && modalEditorElement) {
            modalLanguage = newLang;
            modalLanguageDropdownOpen = false;

            const editor = await modalEditorElement.editorDeferred.promise;
            const editorModel = editor.getModel();
            if (editorModel) {
              (window as any).monaco.editor.setModelLanguage(editorModel, newLang);
            }

            langOptions.forEach(opt => opt.classList.remove('selected'));
            option.classList.add('selected');

            updateToolbarUI(modal);
            updateFooterUI(modal);
          }
        });
      });

      const wrapBtn = toolbar.querySelector('.wrap-btn');
      wrapBtn?.addEventListener('click', async () => {
        modalWordWrap = modalWordWrap === 'on' ? 'off' : 'on';
        if (modalEditorElement) {
          const editor = await modalEditorElement.editorDeferred.promise;
          editor.updateOptions({ wordWrap: modalWordWrap });
        }
        updateToolbarUI(modal);
      });

      const linesBtn = toolbar.querySelector('.lines-btn');
      linesBtn?.addEventListener('click', async () => {
        modalShowLineNumbers = !modalShowLineNumbers;
        if (modalEditorElement) {
          const editor = await modalEditorElement.editorDeferred.promise;
          editor.updateOptions({ lineNumbers: modalShowLineNumbers ? 'on' : 'off' });
        }
        updateToolbarUI(modal);
      });

      const copyBtn = toolbar.querySelector('.copy-btn');
      copyBtn?.addEventListener('click', async () => {
        if (modalEditorElement) {
          const editor = await modalEditorElement.editorDeferred.promise;
          const content = editor.getValue();
          try {
            await navigator.clipboard.writeText(content);
            modalCopySuccess = true;
            updateToolbarUI(modal);
            setTimeout(() => {
              modalCopySuccess = false;
              updateToolbarUI(modal);
            }, 2000);
          } catch (err) {
            console.error('Failed to copy code:', err);
          }
        }
      });

      document.addEventListener('click', (e) => {
        if (modalLanguageDropdownOpen && !langBtn?.contains(e.target as Node)) {
          modalLanguageDropdownOpen = false;
          updateToolbarUI(modal);
        }
      }, { once: true });
    }
  }

  public getValue(): string {
    return this.value;
  }

  public setValue(value: string): void {
    this.value = value;
    if (this.editorElement) {
      this.editorElement.content = value;
      // Also update the Monaco editor directly if it's already loaded
      this.editorElement.editorDeferred.promise.then(editor => {
        if (editor.getValue() !== value) {
          editor.setValue(value);
        }
      });
    }
    this.changeSubject.next(this as any);
  }
}
